'use client';

import { EmptySearch } from '@eliadmin/core/src/components/nt/checkout/empty/empty-search';
import { rememberSearch } from '@eliadmin/core/src/components/nt/header/search/search-history';
import { SearchChips, SearchGroup } from '@eliadmin/core/src/components/nt/header/search/search-ui';
import { Icon } from '@eliadmin/core/src/components/nt/ui/icon';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

import type { SearchTexts } from './search-texts';
import type { SearchViewProps } from './search-types';

const TITLE_ID = 'vyhladavanie-nadpis';
const TEXT_ID = 'vyhladavanie-text';

type SearchEmptyProps = {
  /** `start` = no query yet (the page's H1), `none` = the query found no product (a H2 under the H1). */
  variant: 'start' | 'none';
  texts: SearchTexts;
  panel: SearchViewProps['panel'];
  printerBrandIds: (number | string)[];
};

/**
 * The empty states of the search (no Figma – the card of the empty cart, 168:14017): grey card with
 * the green magnifier, the title and the tip, the printer search (a model opens its category, a
 * code goes to the product search) and the popular searches of the header panel as chips. Without
 * a query the title is the core „Vyhľadávanie“, without products the core „Nenašli sme žiadne
 * vyhovujúce záznamy“; an empty text field hides its line.
 */
export function SearchEmpty({ variant, texts, panel, printerBrandIds }: SearchEmptyProps) {
  const { t } = useTranslations();
  const start = variant === 'start';
  const title = start ? t('text211') : t('text554');
  const text = start ? texts.startText : texts.noneText;
  const Title = start ? 'h1' : 'h2';

  return (
    <div className="flex flex-col items-center gap-6 rounded-2xl bg-[#F5F6F3] px-4 py-10 text-center md:px-12 md:py-14">
      <Icon name="magnifying-glass" size={44} className="text-[#127A26]" />
      <div className="flex max-w-[652px] flex-col items-center gap-2">
        <Title id={TITLE_ID} className="nt-h3 text-balance text-[#0D110D]">
          {title}
        </Title>
        {text ? (
          <p id={TEXT_ID} className="nt-body-l text-[#363D36]">
            {text}
          </p>
        ) : null}
      </div>
      <EmptySearch
        labelledBy={text ? TEXT_ID : TITLE_ID}
        placeholder={panel.placeholder || t('text162')}
        submit={texts.button || t('text1')}
        noResults={t('text554')}
        brandIds={printerBrandIds}
      />
      {panel.popular.items.length ? (
        <SearchGroup title={panel.popular.title} className="items-center [&_ul]:justify-center">
          <SearchChips
            chips={panel.popular.items.map(chip => ({ key: chip.label, ...chip }))}
            icon="trend-up"
            onSelect={rememberSearch}
          />
        </SearchGroup>
      ) : null}
    </div>
  );
}
